import React, { useEffect, useState, useCallback } from 'react';

import { useCurrentProject } from '@adsk/alloy-provider';
import { Box, Flex, Text } from '@adsk/alloy-react-box';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { getThumbnail } from './helpers/utils';
import BlockRemoveButton from '../../BlockRemoveButton';
import UnstyledAnchor from '../../UnstyledAnchor';

const SheetEntityBlock = ({
  entity,
  projectId,
  onRemoveClick,
  onClick,
  onReferenceClickAnalytics,
}) => {
  const [thumbnail, setThumbnail] = useState(null);
  const currentProject = useCurrentProject();
  const scopes = currentProject?.scopes?.project;
  const url = `/build/sheets/projects/${projectId}?moduleId=2d&entityId=${entity.sheetId}`;
  const handleClick = () => {
    onReferenceClickAnalytics?.();
    onClick?.(entity);
  };
  const fetchThumbnail = useCallback(async () => {
    if (!entity.bubbleUrn) return;
    const blob = await getThumbnail(
      entity.bubbleUrn,
      entity.bubbleViewableGuid,
      scopes,
    );
    setThumbnail(blob);
  }, [entity.bubbleUrn, entity.bubbleViewableGuid, scopes]);

  useEffect(() => {
    entity.bubbleUrn &&
      entity.bubbleViewableGuid &&
      !thumbnail &&
      fetchThumbnail();
  }, [entity.bubbleUrn, entity.bubbleViewableGuid, thumbnail, fetchThumbnail]);

  useEffect(() => {
    return () => {
      thumbnail && URL.revokeObjectURL(thumbnail);
    };
  }, [thumbnail]);

  return (
    <UnstyledAnchor
      style={[
        {
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          overflow: 'hidden',
          padding: 10,
          borderBottom: `1px solid ${theme.colors.charcoal100}`,
          ...(handleClick && {
            cursor: 'pointer',
          }),
          ...(onRemoveClick && {
            ['&:hover .SheetEntityBlock__remove-button']: {
              visibility: 'visible',
            },
          }),
        },
      ]}
      onClick={handleClick}
      href={url}
    >
      <Flex
        flex="none"
        alignItems="center"
        justifyContent="center"
        width="40px"
        height="40px"
        marginRight="10px"
        border={`1px solid ${theme.colors.charcoal100}`}
      >
        {thumbnail ? (
          <img width="40" height="40" src={thumbnail} />
        ) : (
          <Box width="100%" height="100%" backgroundColor="charcoal050" />
        )}
      </Flex>
      <Box flex="1" width="80%">
        <OverflowTooltip
          getTargetProps={() => ({
            style: { overflow: 'hidden', flex: 'auto' },
          })}
          content={entity.name}
        >
          <span style={{ fontWeight: 'bold' }}>{entity.name}</span>
        </OverflowTooltip>
        <Flex flexDirection="row">
          <OverflowTooltip
            getTargetProps={() => ({
              style: {
                overflow: 'hidden',
                maxWidth: '100%',
                color: theme.colors.charcoal900,
              },
            })}
            content={entity.versionSet?.name}
          >
            {entity.versionSet?.name}
          </OverflowTooltip>
          {!!entity.collection?.name &&
            entity.collection?.type !== 'default' && (
              <>
                <Text padding="0px 12px" color="charcoal400">
                  |
                </Text>
                <OverflowTooltip
                  getTargetProps={() => ({
                    style: {
                      overflow: 'hidden',
                      maxWidth: '100%',
                      color: theme.colors.charcoal900,
                    },
                  })}
                  content={entity.collection.name}
                >
                  {entity.collection.name}
                </OverflowTooltip>
              </>
            )}
        </Flex>
        <OverflowTooltip
          getTargetProps={() => ({
            style: {
              overflow: 'hidden',
              flex: 1,
              color: theme.colors.charcoal900,
            },
          })}
          content={entity.title}
        >
          {entity.title}
        </OverflowTooltip>
      </Box>
      {onRemoveClick && (
        <Flex alignItems="center" justifyContent="flex-end">
          <BlockRemoveButton
            className="SheetEntityBlock__remove-button"
            style={{
              visibility: 'hidden',
            }}
            onClick={() => onRemoveClick(projectId, entity.id)}
          />
        </Flex>
      )}
    </UnstyledAnchor>
  );
};

export default SheetEntityBlock;
